import { apiFetch, type IdResponse, type OperationResult } from "@/lib/apiClient";
import { fetchAll, pageQuery, toPaged, type Paged, type PageParams } from "@/lib/paging";

interface ProductDetailResponse {
  id: string;
  name: string;
  sku: string | null;
  serialNumber: string | null;
  modelNumber: string | null;
  description: string | null;
  notes: string | null;
  manufacturer: string | null;
  barcode: string | null;
  licenseKey: string | null;
  type: string;
  status: string;
  cost: number;
  priceLevel1: number;
  priceLevel2: number;
  priceLevel3: number;
  priceLevel4: number;
  inventoryQty: number;
  dateInStock: string | null;
  warrantyExpiresAt: string | null;
  dateSold: string | null;
  creationTime: string;
}

function toProductListItem(r: ProductDetailResponse): ProductListItem {
  return {
    id: r.id,
    name: r.name,
    type: r.type,
    sku: r.sku ?? null,
    manufacturer: r.manufacturer ?? null,
    priceLevel1: r.priceLevel1 ?? 0,
    inventoryQty: r.inventoryQty ?? 0,
    status: r.status,
    updatedAt: r.creationTime,
  };
}

function toProductDetail(r: ProductDetailResponse): ProductDetail {
  return {
    id: r.id,
    type: r.type,
    sku: r.sku ?? null,
    serialNumber: r.serialNumber ?? null,
    modelNumber: r.modelNumber ?? null,
    name: r.name,
    description: r.description ?? null,
    notes: r.notes ?? null,
    licenseKey: r.licenseKey ?? null,
    cost: r.cost ?? 0,
    priceLevel1: r.priceLevel1 ?? 0,
    priceLevel2: r.priceLevel2 ?? 0,
    priceLevel3: r.priceLevel3 ?? 0,
    priceLevel4: r.priceLevel4 ?? 0,
    inventoryQty: r.inventoryQty ?? 0,
    barcode: r.barcode ?? null,
    manufacturer: r.manufacturer ?? null,
    dateInStock: r.dateInStock ?? null,
    warrantyExpiresAt: r.warrantyExpiresAt ?? null,
    dateSold: r.dateSold ?? null,
    status: r.status,
    createdAt: r.creationTime,
    updatedAt: r.creationTime,
  };
}

interface SalesCampaignListResponse {
  id: string;
  name: string;
  description: string | null;
  priceLevel: number;
  status: string;
  validFrom: string | null;
  validTo: string | null;
  creationTime: string;
}

function toSalesCampaignListItem(r: SalesCampaignListResponse): SalesCampaignListItem {
  return {
    id: r.id,
    name: r.name,
    priceLevel: r.priceLevel ?? 0,
    status: r.status,
    productCount: 0,
    validFrom: r.validFrom ?? null,
    validTo: r.validTo ?? null,
    updatedAt: r.creationTime,
  };
}

function toSalesCampaignDetail(r: SalesCampaignListResponse): SalesCampaignDetail {
  return {
    id: r.id,
    name: r.name,
    description: r.description ?? null,
    priceLevel: r.priceLevel ?? 0,
    status: r.status,
    validFrom: r.validFrom ?? null,
    validTo: r.validTo ?? null,
    productIds: [],
    userIds: [],
    createdAt: r.creationTime,
    updatedAt: r.creationTime,
  };
}

interface TaxRateListResponse {
  id: string;
  name: string;
  rate: number;
  location: string | null;
  isDefault: boolean;
}

function toTaxRateRow(r: TaxRateListResponse): TaxRateRow {
  return {
    id: r.id,
    name: r.name,
    rate: r.rate ?? 0,
    location: r.location ?? null,
    isDefault: r.isDefault ?? false,
    createdAt: "",
    updatedAt: "",
  };
}

export interface ProductListItem {
  id: string;
  name: string;
  type: string;
  sku: string | null;
  manufacturer: string | null;
  priceLevel1: number;
  inventoryQty: number;
  status: string;
  updatedAt: string;
}

export interface ProductDetail {
  id: string;
  type: string;
  sku: string | null;
  serialNumber: string | null;
  modelNumber: string | null;
  name: string;
  description: string | null;
  notes: string | null;
  licenseKey: string | null;
  cost: number;
  priceLevel1: number;
  priceLevel2: number;
  priceLevel3: number;
  priceLevel4: number;
  inventoryQty: number;
  barcode: string | null;
  manufacturer: string | null;
  dateInStock: string | null;
  warrantyExpiresAt: string | null;
  dateSold: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export const PRODUCT_TYPES = ["Hardware", "SoftwareLicense"] as const;

export const PRODUCT_STATUSES = ["Active", "Discontinued"] as const;

const PRODUCT_SEARCH_COLS = ["name", "sku", "manufacturer", "modelNumber"];

export const products = {
  async list(params: PageParams = {}): Promise<Paged<ProductListItem>> {
    const res = await apiFetch<Paged<ProductDetailResponse> | ProductDetailResponse[]>(
      `/products${pageQuery(params, undefined, PRODUCT_SEARCH_COLS)}`,
    );
    const p = toPaged<ProductDetailResponse>(res);
    return { ...p, items: p.items.map(toProductListItem) };
  },
  listAll(params: PageParams = {}): Promise<ProductListItem[]> {
    return fetchAll((p) => this.list(p), params, "products");
  },
  async get(id: string) {
    const r = await apiFetch<ProductDetailResponse>(`/products/${encodeURIComponent(id)}`);
    return toProductDetail(r);
  },
  create(body: Partial<ProductDetail> & { name: string }) {
    return apiFetch<IdResponse>("/products", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  update(id: string, body: Partial<ProductDetail> & { name: string }) {
    return apiFetch<OperationResult>(`/products/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },
  remove(id: string) {
    return apiFetch<void>(`/products/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },
};

export interface SalesCampaignListItem {
  id: string;
  name: string;
  priceLevel: number;
  status: string;
  productCount: number;
  validFrom: string | null;
  validTo: string | null;
  updatedAt: string;
}

export interface SalesCampaignDetail {
  id: string;
  name: string;
  description: string | null;
  priceLevel: number;
  status: string;
  validFrom: string | null;
  validTo: string | null;
  productIds: string[];

  userIds: string[];
  createdAt: string;
  updatedAt: string;
}

export const SALES_CAMPAIGN_STATUSES = ["Active", "Archived"] as const;

export const PRICE_LEVELS = [1, 2, 3, 4] as const;

export interface TaxRateRow {
  id: string;
  name: string;
  rate: number;
  location: string | null;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SaveTaxRateRequest {
  name: string;
  rate: number;
  location?: string | null;
  isDefault?: boolean;
}

export const taxRates = {
  async list() {
    const res = await apiFetch<Paged<TaxRateListResponse> | TaxRateListResponse[]>("/tax-rates");
    return toPaged<TaxRateListResponse>(res).items.map(toTaxRateRow);
  },
  create(body: SaveTaxRateRequest) {
    return apiFetch<IdResponse>("/tax-rates", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  update(id: string, body: SaveTaxRateRequest) {
    return apiFetch<OperationResult>(`/tax-rates/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },
  remove(id: string) {
    return apiFetch<void>(`/tax-rates/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },
};

const SALES_CAMPAIGN_SEARCH_COLS = ["name"];

export const salesCampaigns = {
  async list(params: PageParams = {}): Promise<Paged<SalesCampaignListItem>> {
    const res = await apiFetch<Paged<SalesCampaignListResponse> | SalesCampaignListResponse[]>(
      `/sales-campaigns${pageQuery(params, undefined, SALES_CAMPAIGN_SEARCH_COLS)}`,
    );
    const p = toPaged<SalesCampaignListResponse>(res);
    return { ...p, items: p.items.map(toSalesCampaignListItem) };
  },
  listAll(params: PageParams = {}): Promise<SalesCampaignListItem[]> {
    return fetchAll((p) => this.list(p), params, "sales-campaigns");
  },
  async get(id: string) {
    const r = await apiFetch<SalesCampaignListResponse>(`/sales-campaigns/${encodeURIComponent(id)}`);
    return toSalesCampaignDetail(r);
  },
  create(body: Partial<SalesCampaignDetail> & { name: string }) {
    return apiFetch<IdResponse>("/sales-campaigns", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  update(id: string, body: Partial<SalesCampaignDetail> & { name: string }) {
    return apiFetch<OperationResult>(`/sales-campaigns/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },
  remove(id: string) {
    return apiFetch<void>(`/sales-campaigns/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },
};
